import axios from 'axios';
import { useCallback, useMemo } from 'react';
import { createQuery } from 'react-query-kit';

import { useCommunicationChannelData } from '@/pages/communicationChannel/communicationChannel.queries';
import { useAllContactData } from '@/pages/contact/contact.queries';
import { appendEscapeParam, encodePathSegment, restUrl } from '@/services/utils';
import { RealTimeMessagingEvents } from '@/types/RealTimeMessagingEvents';

import useRealTimeMessaging from './useRealtimeMessaging';

/**
 * Same codes as {@code ItemPublication.PublishedStatus} / the Angular
 * {@code PublicationStatusController}, which keeps only {@code PUBLISHED}.
 */
export const PUBLISHED_STATUS = 'PUBLISHED' as const;
export const UNPUBLISHED_STATUS = 'UNPUBLISHED' as const;

export type PublishedStatus = typeof PUBLISHED_STATUS | typeof UNPUBLISHED_STATUS;

export type DestinationType = 'IN_PLATFORM' | 'COMMUNICATION_CHANNEL';

export type ItemPublication = {
    primaryKey?: string;
    destinationId: number;
    subDestination?: string;
    destinationType?: DestinationType;
    publicationTaskId?: number;
    publishedAt?: number | string;
    publishedBy?: string;
    publishedStatus?: PublishedStatus;
};

export type PublicationOrganization = {
    organizationId?: number;
    id?: number;
    name?: string;
    imageUrl?: string;
    address?: string;
    email?: string;
    url?: string;
};

export type PublicationChannel = {
    id?: number;
    name?: string;
    type?: string;
    plan?: string;
    planLabel?: string;
    format?: string;
    subDestinations?: Record<string, string>;
    subDestinationKey?: string;
    subDestinationLabel?: string;
};

export type EnrichedItemPublication = ItemPublication & {
    organization?: PublicationOrganization;
    communicationChannel?: PublicationChannel;
};

export type UsePublicationStatusResult = {
    status: EnrichedItemPublication[];
    isLoading: boolean;
    refetch: () => Promise<unknown>;
};

const PATH_ESCAPE = '~';

const useItemPublicationsQuery = createQuery<ItemPublication[], { primaryKey: string }>({
    queryKey: ['itemPublications'],
    fetcher: async ({ primaryKey }) => {
        const url = appendEscapeParam(
            restUrl(`items/${encodePathSegment(primaryKey, PATH_ESCAPE)}/publications`),
            PATH_ESCAPE,
            primaryKey,
        );
        const response = await axios.get<ItemPublication[]>(url);
        return response.data ?? [];
    },
});

const findOrganization = (
    destinationId: number,
    organizations: readonly PublicationOrganization[],
): PublicationOrganization | undefined =>
    organizations.find((organization) => organization.organizationId === destinationId);

const findChannel = (
    publication: ItemPublication,
    channels: readonly PublicationChannel[],
): PublicationChannel | undefined => {
    const exact = channels.find(
        (channel) =>
            channel.id === publication.destinationId
            && (!publication.subDestination
                || publication.subDestination === channel.subDestinationKey),
    );
    if (exact) {
        return exact;
    }

    const parent = channels.find((channel) => channel.id === publication.destinationId);
    if (!parent || !publication.subDestination) {
        return parent;
    }

    return {
        ...parent,
        subDestinationKey: publication.subDestination,
        subDestinationLabel: parent.subDestinations?.[publication.subDestination],
    };
};

/**
 * Ports {@code PublicationStatusController#loadPublications}: keep PUBLISHED
 * rows and attach the matching organization or communication channel.
 */
export const enrichItemPublications = (
    publications: readonly ItemPublication[] | undefined,
    organizations: readonly PublicationOrganization[] | undefined,
    channels: readonly PublicationChannel[] | undefined,
): EnrichedItemPublication[] => {
    const orgs = organizations ?? [];
    const chans = channels ?? [];

    return (publications ?? [])
        .filter((publication) => publication.publishedStatus === PUBLISHED_STATUS)
        .map((publication) => {
            const isInPlatform =
                publication.destinationType === 'IN_PLATFORM'
                || (publication.destinationType == null && publication.subDestination == null);
            const isChannel =
                publication.destinationType === 'COMMUNICATION_CHANNEL'
                || (publication.destinationType == null && publication.subDestination != null);

            let organization: PublicationOrganization | undefined;
            let communicationChannel: PublicationChannel | undefined;

            if (isInPlatform) {
                organization = findOrganization(publication.destinationId, orgs);
            } else if (isChannel) {
                communicationChannel = findChannel(publication, chans);
            }

            if (!organization && !communicationChannel) {
                organization = findOrganization(publication.destinationId, orgs);
                if (!organization) {
                    communicationChannel = findChannel(publication, chans);
                }
            }

            return {
                ...publication,
                organization,
                communicationChannel,
            };
        });
};

const containsNeedle = (value: unknown, needle: string, depth = 0): boolean => {
    if (depth > 4 || value == null) {
        return false;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value).toLowerCase().includes(needle);
    }
    if (typeof value === 'object') {
        return Object.values(value as Record<string, unknown>).some((nested) =>
            containsNeedle(nested, needle, depth + 1),
        );
    }
    return false;
};

/** AngularJS {@code filter:publicationFilter} — case-insensitive substring over nested fields. */
export const matchesPublicationFilter = (
    publication: EnrichedItemPublication,
    query: string,
): boolean => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return true;
    }
    return containsNeedle(publication, needle);
};

/**
 * LAX-10152: React port of {@code publication-status-controller.js}.
 * Loads an item's publications, keeps the PUBLISHED rows, and refetches
 * when a publication job changes over the realtime channel.
 */
const usePublicationStatus = (primaryKey: string): UsePublicationStatusResult => {
    const publicationsQuery = useItemPublicationsQuery({
        variables: { primaryKey },
        enabled: Boolean(primaryKey),
    });

    const contactsQuery = useAllContactData(Boolean(primaryKey));
    const channelsQuery = useCommunicationChannelData(Boolean(primaryKey));

    const refetch = useCallback(() => publicationsQuery.refetch(), [publicationsQuery]);

    const onJobChanged = useCallback(() => {
        void refetch();
    }, [refetch]);

    useRealTimeMessaging({
        eventName: RealTimeMessagingEvents.PUBLICATION_JOB_CHANGED_EVENT,
        eventHandler: onJobChanged,
    });

    const status = useMemo(
        () =>
            enrichItemPublications(
                publicationsQuery.data,
                contactsQuery.data as PublicationOrganization[] | undefined,
                channelsQuery.data as PublicationChannel[] | undefined,
            ),
        [publicationsQuery.data, contactsQuery.data, channelsQuery.data],
    );

    const isLoading =
        Boolean(primaryKey)
        && (publicationsQuery.isLoading || contactsQuery.isLoading || channelsQuery.isLoading);

    return {
        status,
        isLoading,
        refetch,
    };
};

export { usePublicationStatus };
export default usePublicationStatus;
